import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { congesApi, type LigneJustificatifSuivi } from '../../services/conges-api';
import { MessageErreur, MessageVide } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale, messageRefusPiece } from '../../components/ndf/ui/erreurs-champs';
import { BoutonPrincipal, BoutonSecondaire, Saisie } from '../../components/ndf/ui/Champ';
import { Link } from 'react-router-dom';
import { periodeFr } from '../../lib/periode-conges';
import { jourAParis } from '../../../server/lib/dates';
import { dateFr } from './CongesDemandesPage';

/**
 * Le suivi des justificatifs différés (R-ABS-07) — écran de la RH.
 *
 * POURQUOI il existe. Certains justificatifs n'existent pas quand on pose son
 * congé : l'acte de décès est remis le jour des obsèques. L'absence part donc
 * sans preuve, et c'est CETTE liste qui garantit qu'elle finit par arriver.
 * Sans elle, on se retrouverait avec des absences non justifiées sans aucun
 * moyen de le voir — et surtout, on oublierait.
 *
 * Ce qui demande une action vient en TÊTE : les pièces déposées, à vérifier,
 * avant celles qui manquent encore et dont le salarié est relancé tout seul.
 */

/**
 * Les trois états que cette liste peut porter — et « validé » n'en fait pas
 * partie : `aSuivre` l'écarte côté serveur, puisqu'il n'y a plus rien à faire.
 * Le type le dit, plutôt qu'une entrée « Validé » qui ne s'afficherait jamais
 * et laisserait croire que l'écran sait montrer un historique. Il ne le sait
 * pas : une pièce validée se relit depuis la demande.
 */
type StatutSuivi = Exclude<LigneJustificatifSuivi['statut'], 'valide'>;

const TEINTE: Record<StatutSuivi, string> = {
  depose: 'bg-jaune-fond/40 text-jaune',
  attendu: 'bg-ciel/30 text-nuit',
  refuse: 'bg-corail-fond/40 text-corail',
};

const LIBELLE: Record<StatutSuivi, string> = {
  depose: 'À vérifier',
  attendu: 'Attendu',
  refuse: 'Refusé, à remplacer',
};

/**
 * Le jour d'un HORODATAGE, écrit à la française.
 *
 * `depose_at` et `relance_at` sont des instants, pas des dates, et la relance
 * part quand le planificateur passe : tôt le matin. Découper la chaîne ISO lit
 * le jour en temps universel, donc une relance de 00h30 parisienne s'affichait
 * à la VEILLE — la RH lisait « dernière relance le 19/03 » pour un courriel
 * parti le 20. Le jour se lit à Paris (`server/lib/dates`), puis s'écrit avec le
 * `dateFr` que les autres écrans Congés utilisent déjà.
 */
const jourFr = (iso: string | null): string => (iso ? dateFr(jourAParis(new Date(iso))) : '—');

export function CongesJustificatifsPage() {
  const qc = useQueryClient();
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ['conges', 'justificatifs'],
    queryFn: () => congesApi.demandes.justificatifsASuivre(),
  });

  const decider = useMutation({
    mutationFn: (v: { id: string; valide: boolean; motif?: string }) =>
      congesApi.demandes.deciderJustificatif(v.id, { valide: v.valide, motif: v.motif }),
    onSuccess: () => {
      setErreur(null);
      void qc.invalidateQueries({ queryKey: ['conges', 'justificatifs'] });
    },
    onError: (e) => setErreur(messageRefusPiece(e) ?? erreurGlobale(e) ?? 'L’action a échoué.'),
  });

  if (liste.isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (liste.isError) return <div className="p-6"><MessageErreur erreur={liste.error} /></div>;

  const lignes = liste.data?.justificatifs ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6">
      <header>
        <h1 className="font-titre text-xl font-bold text-nuit">Justificatifs</h1>
        <p className="text-sm text-nuit/60">
          Les absences dont la pièce ne pouvait pas être fournie à l’avance — un acte de décès est
          remis le jour des obsèques. Le salarié est relancé trois jours ouvrés après la fin de son
          absence, puis chaque semaine, avec son manager et vous en copie.
        </p>
      </header>

      {erreur && (
        <p className="rounded-lg border border-corail-fond/60 bg-white p-3 text-sm text-corail">{erreur}</p>
      )}

      {lignes.length === 0 ? (
        <MessageVide>
          Aucun justificatif en attente. Rien à vérifier, rien à réclamer.
        </MessageVide>
      ) : (
        <ul className="space-y-3">
          {lignes.map((l) => (
            <li key={l.demandeId} className="rounded-lg border border-nuit/10 bg-white p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium text-nuit">{l.salarie}</p>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    TEINTE[l.statut as StatutSuivi] ?? 'bg-ciel/30 text-nuit'
                  }`}
                >
                  {LIBELLE[l.statut as StatutSuivi] ?? 'À regarder'}
                </span>
              </div>
              <p className="text-sm text-nuit/70">
                {l.type}, {periodeFr({ date_debut: l.debut, date_fin: l.fin, debut_demi: l.debut_demi, fin_demi: l.fin_demi })}
              </p>
              <p className="mt-1 text-xs text-nuit/50">
                {l.pieces > 0 ? `${l.pieces} pièce(s) déposée(s) le ${jourFr(l.depose_at)}` : 'Aucune pièce'}
                {l.relance_at && ` · dernière relance le ${jourFr(l.relance_at)}`}
              </p>
              {l.motif && <p className="mt-1 text-xs text-corail">Refus précédent : {l.motif}</p>}

              {/* Le lien vaut pour TOUTES les lignes : la RH peut déposer à la
                  place du salarié — elle reçoit parfois le document par un autre
                  canal — et elle doit pouvoir ouvrir la pièce déjà là. */}
              <Link to={`/conges/demandes/${l.demandeId}`} className="mt-1 inline-block text-sm text-nuit underline">
                Ouvrir la demande{l.pieces > 0 ? ' et sa pièce' : ''}
              </Link>

              {/* La décision n'est proposée que sur une pièce DÉPOSÉE : il n'y a
                  rien à valider tant que rien n'est arrivé. */}
              {l.statut === 'depose' && (
                <div className="mt-3 space-y-2 border-t border-nuit/10 pt-3">
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="flex-1 text-sm">
                      <span className="mb-1 block text-nuit/70">Motif, si vous refusez</span>
                      <Saisie
                        value={motifs[l.demandeId] ?? ''}
                        onChange={(e) => setMotifs((m) => ({ ...m, [l.demandeId]: e.target.value }))}
                        placeholder="Ce document ne mentionne pas la date des obsèques"
                      />
                    </label>
                    <BoutonPrincipal
                      type="button"
                      disabled={decider.isPending}
                      onClick={() => decider.mutate({ id: l.demandeId, valide: true })}
                    >
                      Valider
                    </BoutonPrincipal>
                    <BoutonSecondaire
                      type="button"
                      disabled={decider.isPending}
                      onClick={() =>
                        decider.mutate({ id: l.demandeId, valide: false, motif: motifs[l.demandeId] ?? '' })
                      }
                    >
                      Refuser
                    </BoutonSecondaire>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
